import clsx from 'clsx';
import { useState } from 'react';
import { useForm } from 'react-hook-form';

import type { WithFormErrors } from '@/components/Forms/FormControl';
import FormControl from '@/components/Forms/FormControl';
import FormErrors from '@/components/Forms/FormErrors';
import FormSubmitButton from '@/components/Forms/FormSubmitButton';
import TextArea from '@/components/Forms/TextArea';
import useFormSubmit from '@/components/Forms/useFormSubmit';
import useClientInteractive from '@/renderer/utils/useInteractive';
import useRecaptcha from '@/renderer/utils/useRecaptcha';
import { useRequest } from '@/renderer/utils/useRequest';
import type { RequestSchema } from '@/types/request';
import { ArrowRightIcon, CheckCircleIcon, ChevronRightIcon } from '@heroicons/react/20/solid';

// Types
type AffiliateApplication = {
  name: string;
  email: string;
  xAccount?: string;
  howYouWillPromote?: string;
  additionalInfo?: string;
  recaptcha: string;
};

type AffiliateFormData = API.WithData<AffiliateApplication>;

type AffiliateFormState = {
  message: string;
};

// API requests TODO
const affiliateApplicationRequest: RequestSchema<AffiliateFormState, AffiliateFormData> = {
  name: 'affiliateApplicationRequest',
  input: '/api/v1/affiliate/apply',
  method: 'POST',
};

export default function AffiliateForm() {
  const form = useForm<WithFormErrors<AffiliateApplication>>();

  // Request hooks
  const request = useRequest(affiliateApplicationRequest, { manual: true, hideAlert: true });

  const [isSubmitted, setIsSubmitted] = useState<boolean>(false);

  const clientSideInteractive = useClientInteractive();
  const { loading: recaptchaLoading, generateToken: generateRecaptchaToken } = useRecaptcha();

  // Handlers
  const handleFormSubmit = useFormSubmit(
    {
      form: form,
      callback: async (body) => {
        setIsSubmitted(false);
        const result = await request.run({ data: body }, { throwOnRun: true });

        let recaptcha = '';

        try {
          recaptcha = await generateRecaptchaToken('submit');
        } catch (error) {
          form.setError('recaptcha', {
            type: 'manual',
            message: error instanceof Error ? error.message : String(error),
          });

          return;
        }

        if (!recaptcha) {
          form.setError('recaptcha', {
            type: 'manual',
            message: 'Failed to load CAPTCHA code. Please refresh or contact a site administrator.',
          });
        }

        if (
          result?.message === 'API.INVALID.X.ACCOUNT' ||
          result?.message === 'API.INVALID.X.VERIFICATION.FAILED'
        ) {
          setIsSubmitted(false);
          form.setError('xAccount', {
            type: 'manual',
            message: 'X account is invalid. Please check your username.',
          });
        } else if (result?.message === 'API.AFFILIATE.APPLICATION.SUCCESS') {
          setIsSubmitted(true);
          form.reset();
        } else {
          setIsSubmitted(false);
        }
      },
    },
    [],
  );

  return isSubmitted ? (
    <div className="my-24 text-center">
      <CheckCircleIcon height={64} className="mx-auto size-16 text-emerald-600" />
      <p className="mx-auto mt-4 w-3/4 font-medium text-lg sm:w-auto">
        Thank you for applying to our affiliate program!
      </p>
      <p className="text-sm text-zinc-500">
        We’ve received your submission and will review it shortly. If your application is accepted,
        you’ll hear from us via email with the next steps.
      </p>
    </div>
  ) : (
    <form onSubmit={handleFormSubmit} method="POST" className="space-y-6">
      <FormControl form={form} name="name" label="Name*" required autoComplete="name" />

      <FormControl form={form} name="email" label="Email address*" required autoComplete="email" />

      <FormControl form={form} name="xAccount" label="X (Twitter) account (optional)" />

      <FormControl
        form={form}
        name="howYouWillPromote"
        label="How do you plan to promote us?"
        wrap={TextArea}
        rows={6}
      />

      <FormControl
        form={form}
        name="additionalInfo"
        label="Any additional information you want to share? (optional)"
        wrap={TextArea}
        rows={6}
      />

      <FormErrors
        form={form}
        request={request}
        rewrite={(err) => {
          return err.toString();
        }}
      />

      <div className="flex items-center space-x-4">
        <div className="w-48 min-w-[12rem]">
          <FormSubmitButton
            disabled={
              request.loading ||
              !clientSideInteractive ||
              recaptchaLoading ||
              !form.formState.isDirty
            }
            loading={request.loading || !clientSideInteractive || recaptchaLoading}
            className={clsx('items-center justify-center space-x-0.5', {
              group: !(request.loading || !form.formState.isDirty),
            })}
          >
            <span> {!clientSideInteractive ? 'Please wait' : 'Apply now'}</span>
            <ChevronRightIcon className="size-5 shrink-0 group-hover:hidden" />
            <ArrowRightIcon className="mx-px hidden size-5 p-px transition-transform group-hover:block" />
          </FormSubmitButton>
        </div>
        <div
          className={clsx(
            'text-zinc-500 leading-4 transition-opacity duration-500',
            isSubmitted ? 'opacity-100' : 'opacity-0',
          )}
        >
          Application submitted!
        </div>
      </div>
    </form>
  );
}
